import { ExportEngine, type ExportColumn } from '@memberjunction/export-engine';
import { CRITERION_SHORT } from '../shared/format';
import { OEPDownloadExport } from '../shared/download';
import type { OEPCommNominee, OEPCommScoreInput } from './committee.vm';

/** One row of the committee score sheet: text cells are strings, means are numbers ('' when there is nothing to average). */
export type OEPScoreRow = Record<string, string | number>;

/** The sheet's columns, in order (task 28 brief "Export"). */
export const OEP_SCORE_COLUMNS: readonly string[] = [
    'Code', 'Title', 'Presenter', 'Institution', 'Career stage', 'Nominator(s)',
    ...CRITERION_SHORT.map((c) => `Nomination: ${c}`),
    ...CRITERION_SHORT.map((c) => `Review: ${c}`),
    'Picked',
];

/** Mean of one criterion (Sequence) across score sets, rounded to 2 decimals; '' when no set scored it. */
function mean(sets: readonly (readonly OEPCommScoreInput[])[], sequence: number): number | '' {
    const values = sets.flatMap((s) => s.filter((x) => x.Sequence === sequence && x.Level !== null).map((x) => x.Level as number));
    if (values.length === 0) return '';
    return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100;
}

/**
 * The score sheet, one row per nominee: nomination means over that presentation's Submitted
 * nominations, review means over its released reviews, and whether it is on the slate.
 */
export function BuildScoreRows(nominees: readonly OEPCommNominee[]): OEPScoreRow[] {
    return nominees.map((n) => {
        const row: OEPScoreRow = {
            'Code': n.Code ?? '',
            'Title': n.Title,
            'Presenter': n.PresenterName,
            'Institution': n.Institution ?? '',
            'Career stage': n.CareerStage ?? '',
            'Nominator(s)': n.Nominations.map((x) => x.NominatorName).join('; '),
        };
        CRITERION_SHORT.forEach((c, idx) => { row[`Nomination: ${c}`] = mean(n.Nominations.map((x) => x.Scores), idx + 1); });
        CRITERION_SHORT.forEach((c, idx) => { row[`Review: ${c}`] = mean(n.Reviews.map((x) => x.Scores), idx + 1); });
        row['Picked'] = n.ItemID !== null ? 'yes' : 'no';
        return row;
    });
}

/** "OEP-{SeasonCode}-{Section}-scores" (task 28 brief); characters a file name cannot hold become "-". */
export function ScoreFileName(seasonCode: string, sectionName: string): string {
    return `OEP-${seasonCode}-${sectionName}-scores`.replace(/[\\/:*?"<>|]+/g, '-');
}

/**
 * Builds the .xlsx with ExportEngine.toExcel and hands it to the browser as a download.
 *
 * Formula safety (verified in node_modules, not assumed): ExportEngine's single-sheet path passes each
 * value through BaseExporter.formatValue (export-engine/dist/base-exporter.js:119-144, strings returned
 * unchanged) into ExcelJS worksheet.addRow (excel-exporter.js:90). ExcelJS types a JS string as
 * ValueType.String (exceljs/lib/doc/cell.js:1066-1067) and writes it as a shared string `t="s"`
 * (exceljs/lib/xlsx/xform/sheet/cell-xform.js:230-234) — a formula cell is only produced for an object
 * value with `formula`/`sharedFormula` (cell.js:1081-1083) or an explicit `formulas` option, neither of
 * which this sheet uses (OEPScoreRow is string | number). So "=…", "+…", "-…", "@…" stay text;
 * committee-export.spec.ts reads a generated workbook back to prove it.
 *
 * Columns are passed explicitly: derived columns would re-case the headers
 * (BaseExporter.formatColumnName, base-exporter.js:39-46 turns "Nominator(s)" into "Nominator(S)").
 * The download itself is OEPDownloadExport (shared/download.ts).
 */
export async function DownloadScores(rows: readonly OEPScoreRow[], fileName: string): Promise<void> {
    const columns: ExportColumn[] = OEP_SCORE_COLUMNS.map((c) => ({ name: c, displayName: c }));
    const result = await ExportEngine.toExcel([...rows], { fileName, columns, sheetName: 'Scores' });
    OEPDownloadExport(result, fileName, 'excel', 'Couldn\'t build the score sheet.');
}
